// ServiceHealthOverlay.tsx
import React from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import "./ServiceHealthOverlay.css";

interface Props {
  open: boolean;
  gatewayStatus: "UP" | "DOWN" | "UNKNOWN";
  downServices: string[];
  error?: string;
}

const ServiceHealthOverlay: React.FC<Props> = ({
  open,
  gatewayStatus,
  downServices,
  error,
}) => {
  if (!open) return null;

  const anyDown = downServices.length > 0;
  const gatewayDown = gatewayStatus === "DOWN";

  let title = "Service Issues Detected";
  let description =
    "The application cannot be used right now. Please try again after some time.";

  if (gatewayDown && !anyDown) {
    title = "API Gateway Unreachable";
    description =
      "The system is temporarily unavailable. You cannot use the application right now. Please try again later.";
  } else if (anyDown) {
    title = "Some Services Are Down";
    description =
      "The application is currently unavailable due to service interruptions. Please try again later.";
  }

  if (error && !anyDown && !gatewayDown) {
    description = `The system is currently unavailable due to an internal error. Please try again later. Error details: ${error}`;
  }

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      hideBackdrop={false}
      PaperProps={{
        className: "sho-paper",
        elevation: 3, // matches boxShadow: 3 from sx
      }}
      BackdropProps={{
        className: "sho-backdrop",
      }}
      className="sho-dialog"
    >
      <DialogContent className="sho-dialog-content">
        <Box className="sho-content-box">
          <WarningAmberIcon className="sho-warning-icon" />

          <Typography className="sho-title">{title}</Typography>

          <Typography className="sho-description">{description}</Typography>

          <Box className="sho-status-box">
            <CircularProgress size={16} />
            <Typography className="sho-status-text">
              Checking service status…
            </Typography>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceHealthOverlay;
